import { Box } from "@mui/material";
import { macro } from "../ui/macroTokens";
import { v2, nums } from "../ui/v2Tokens";
import { macroFigure, macroText } from "../ui/nutritionUnits";
import { useNutritionDisplayMode } from "../../../../contexts/NutritionDisplayContext";
import { QUALITY_PROTEIN_PER_MEAL_G, type MacroTotals } from "../model/nutritionV2Model";

/**
 * Where a meal's quality protein stands, on the meal itself.
 *
 * The daily targets bar already reports quality protein for the whole day, but a day can hit
 * its protein target with three of its five meals carrying almost none — and the daily figure
 * cannot show that. This puts the same number where the trainer can act on it: under the meal
 * whose foods they are currently choosing.
 *
 * It is a reading, not a validation. Nothing is blocked by falling short, and a snack that
 * carries no protein is not a mistake — hence "חסרים X" rather than an error tone.
 *
 * Quality protein is protein, so in points mode every figure here — the reading, the target and
 * what is missing — is protein points. The share is a ratio and reads the same in either unit.
 */
const QualityProteinLine = ({ totals }: { totals: MacroTotals }) => {
  const quality = totals.qualityProtein;
  const share = totals.protein > 0 ? Math.round((quality / totals.protein) * 100) : 0;
  const pct = Math.min(100, (quality / QUALITY_PROTEIN_PER_MEAL_G) * 100);
  const ok = quality >= QUALITY_PROTEIN_PER_MEAL_G;
  const mode = useNutritionDisplayMode();
  const reading = macroFigure("protein", quality, mode);

  return (
    <Box
      data-testid="quality-protein-line"
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "8px",
        flexWrap: "wrap",
        padding: "11px 16px",
        borderTop: `1px solid ${v2.lineSoft}`,
        // Grown with the total row above it, but by less: this is a reading ABOUT the total, and
        // matching its size would make the two compete instead of reading as a line and its note.
        fontSize: "13.5px",
        color: v2.ink3,
        ...nums,
      }}
    >
      <Box component="span">
        חלבון איכותי בארוחה{" "}
        <Box component="span" sx={{ color: macro.protein.text, fontWeight: 600 }}>
          {reading.text}
        </Box>{" "}
        {reading.unit} · {share}% מהחלבון
      </Box>

      <Box
        sx={{
          flex: "none",
          width: "74px",
          height: "4px",
          borderRadius: "99px",
          background: "#EDF1F4",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            height: "100%",
            width: `${pct}%`,
            borderRadius: "99px",
            background: ok ? v2.brand : macro.protein.text,
            transition: "width .3s",
          }}
        />
      </Box>

      <Box
        sx={{
          marginInlineStart: "auto",
          fontSize: "12px",
          color: ok ? v2.brandText : v2.warn,
        }}
      >
        {ok
          ? `עומד ביעד ${macroText("protein", QUALITY_PROTEIN_PER_MEAL_G, mode)}`
          : `חסרים ${macroText("protein", QUALITY_PROTEIN_PER_MEAL_G - quality, mode)} ליעד`}
      </Box>
    </Box>
  );
};

export default QualityProteinLine;
